import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { cn } from "@f2e/ui/lib/utils"

export interface UsageBarProps {
  creditsBalance: number
  monthlyLimit: number
  className?: string
  title?: string
  description?: string
}

const UsageBar = ({
  creditsBalance,
  monthlyLimit,
  className,
  title,
  description,
}: UsageBarProps) => {
  const { t } = useTranslation()
  const formatCredits = (value: number) =>
    new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  const { percent, used } = useMemo(() => {
    const limit = Math.max(0, monthlyLimit)
    const remaining = Math.max(0, creditsBalance)
    const usedCredits = Math.max(0, limit - remaining)
    const ratio = limit > 0 ? Math.min(1, usedCredits / limit) : 0
    return { percent: ratio * 100, used: usedCredits }
  }, [creditsBalance, monthlyLimit])

  const barColor =
    percent >= 100
      ? "bg-red-500"
      : percent >= 80
        ? "bg-yellow-500"
        : "bg-green-500"

  return (
    <div
      className={cn(
        "space-y-3 rounded-lg border border-muted/60 bg-muted/20 p-4",
        className
      )}
    >
      <div className="flex items-start justify-between gap-3 text-sm">
        <div className="space-y-1">
          <div className="font-medium text-foreground">
            {title ?? t("billing.usageBar.title")}
          </div>
          {description ? (
            <div className="text-xs text-muted-foreground">{description}</div>
          ) : null}
        </div>
        <span className="font-medium text-muted-foreground whitespace-nowrap">
          {t("billing.usageBar.value", {
            used: formatCredits(used),
            limit: formatCredits(monthlyLimit),
          })}
        </span>
      </div>
      <div className="h-3 w-full rounded-full bg-muted">
        <div
          className={cn("h-3 rounded-full transition-all", barColor)}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  )
}

export default UsageBar
